import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Clock, Camera } from 'lucide-react'
import { formatRelativeTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'

interface Face {
  personId: string | null
  personName: string
  classificationId: string
  bbox: { x: number; y: number; width: number; height: number }
  isUnmatched?: boolean
  isNewFace?: boolean
}

export interface Sighting {
  id: string
  cameraId: string
  deviceId: string
  deviceName: string
  cameraName?: string
  timestamp: string
  frameUrl: string
  faces: Face[]
  metadata: {
    totalFaces: number
    alertSent: boolean
    hasNewFaces: boolean
    hasUnmatchedFaces?: boolean
  }
}

interface ClassificationInfo {
  color: string
  displayName: string
}

interface FeedSightingCardProps {
  sighting: Sighting
  classifications: Map<string, ClassificationInfo>
  isNewArrival?: boolean
  /**
   * Called when a face is tapped to open the action drawer. Provides the face
   * plus a base64-encoded JPEG crop of just that face (so the receptionist's
   * "add as new" flow can use it as the person photo).
   */
  onFaceTap: (
    face: Face,
    cropBase64: string | null,
    sighting: Sighting
  ) => void
}

/**
 * One sighting in the live feed. Mobile-first: full-width card, annotated
 * frame fills the card top, tappable face chips below.
 */
export function FeedSightingCard({
  sighting,
  classifications,
  isNewArrival,
  onFaceTap,
}: FeedSightingCardProps) {
  const { t, i18n } = useTranslation()
  const imageRef = useRef<HTMLImageElement>(null)
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null)
  const [imgLoaded, setImgLoaded] = useState(false)

  useEffect(() => {
    setImgLoaded(false)
    setImgDims(null)
  }, [sighting.frameUrl])

  const cropFace = (face: Face): string | null => {
    const img = imageRef.current
    if (!img || !img.complete || !imgDims) return null
    try {
      const pad = Math.round(Math.max(face.bbox.width, face.bbox.height) * 0.2)
      const sx = Math.max(0, face.bbox.x - pad)
      const sy = Math.max(0, face.bbox.y - pad)
      const sw = Math.min(imgDims.w - sx, face.bbox.width + pad * 2)
      const sh = Math.min(imgDims.h - sy, face.bbox.height + pad * 2)
      const canvas = document.createElement('canvas')
      const target = 256
      const scale = Math.min(1, target / Math.max(sw, sh))
      canvas.width = Math.round(sw * scale)
      canvas.height = Math.round(sh * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      return dataUrl.split(',')[1] ?? null
    } catch (err) {
      console.warn('Face crop failed:', err)
      return null
    }
  }

  return (
    <Card
      className={cn(
        'relative overflow-hidden border border-rule bg-paper-2/40 p-0 shadow-sm transition',
        isNewArrival && 'animate-in fade-in slide-in-from-top-2 duration-300'
      )}
    >
      {/* Slow-fading highlight ring for newly-arrived sightings (≈60s). */}
      {isNewArrival && (
        <div className="pointer-events-none absolute inset-0 z-20 animate-feed-highlight" />
      )}

      {/* Annotated frame */}
      <div className="relative w-full overflow-hidden bg-ink">
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          ref={imageRef}
          src={sighting.frameUrl}
          alt={t('sightings.sightingDetails')}
          crossOrigin="anonymous"
          onLoad={(e) => {
            const el = e.currentTarget
            setImgDims({ w: el.naturalWidth, h: el.naturalHeight })
            setImgLoaded(true)
          }}
          className="block aspect-video w-full bg-ink object-cover"
        />

        {/* Face bbox overlays */}
        {imgLoaded && imgDims && sighting.faces.map((face, idx) => {
          const info = classifications.get(face.classificationId)
          const color = face.isUnmatched ? '#888' : info?.color || '#999'
          const left = (face.bbox.x / imgDims.w) * 100
          const top = (face.bbox.y / imgDims.h) * 100
          const w = (face.bbox.width / imgDims.w) * 100
          const h = (face.bbox.height / imgDims.h) * 100
          return (
            <div
              key={idx}
              className="pointer-events-none absolute box-border"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${w}%`,
                height: `${h}%`,
                border: `2px ${face.isUnmatched ? 'dashed' : 'solid'} ${color}`,
                boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
              }}
            />
          )
        })}

        {/* Top-right alert badge */}
        {sighting.metadata.alertSent && (
          <Badge variant="destructive" className="absolute right-2 top-2 text-[10px]">
            {t('sightings.alertSent')}
          </Badge>
        )}

        {/* Bottom strip: time + camera */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-[11px] text-white">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            <span>{formatRelativeTime(sighting.timestamp, i18n.language)}</span>
          </div>
          <div className="flex items-center gap-1.5 truncate">
            <Camera className="h-3 w-3 shrink-0" />
            <span className="truncate">{sighting.cameraName || sighting.deviceName}</span>
          </div>
        </div>
      </div>

      {/* Person chips */}
      <div className="flex flex-wrap gap-1.5 px-3 py-3">
        {sighting.faces.length === 0 && (
          <span className="text-[12px] italic text-ink-soft">
            {t('feed.card.noFaces')}
          </span>
        )}
        {sighting.faces.map((face, idx) => {
          const info = classifications.get(face.classificationId)
          const color = face.isUnmatched ? '#888' : info?.color || '#999'
          const label = face.isUnmatched
            ? t('sightings.unidentified')
            : face.personName || t('common.unknown')
          return (
            <button
              key={idx}
              type="button"
              onClick={() => onFaceTap(face, cropFace(face), sighting)}
              className={cn(
                'inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] transition',
                'hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                face.isUnmatched
                  ? 'border-dashed border-ink-soft bg-paper-2 italic text-ink-soft'
                  : 'border-rule bg-paper text-ink'
              )}
              style={!face.isUnmatched ? { borderColor: color } : undefined}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="font-medium">{label}</span>
              {face.isNewFace && !face.isUnmatched && (
                <span className="text-[10px] uppercase tracking-wide text-gold">
                  {t('sightings.new')}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </Card>
  )
}
